import type { FC, ReactNode } from 'react';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, xcss, Inline, Flex } from '@atlaskit/primitives';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { N10 } from '@atlaskit/theme/colors';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { SummaryAnalyticsPropertiesType } from '@confluence/quick-summary';

const i18n = defineMessages({
	showMore: {
		id: 'ai-comments-summary.content-blur.show-more',
		defaultMessage: 'Show more',
		description: 'Button used to expand the summary section when clicked',
	},
});

export const SUMMARY_LINE_HEIGHT = 20;

const flexContainerStyles = xcss({
	position: 'relative',
});

const collapsedContainerPadding = xcss({
	paddingBottom: 'space.150',
	paddingRight: 'space.150',
});

const contentBlurStyles = xcss({
	background: `linear-gradient( 0deg, ${token('elevation.surface.overlay', N10)} 0%, ${token(
		'color.background.neutral.subtle',
		'rgba(255, 255, 255, 0)',
	)} 100% )`,
	height: '20px',
	position: 'absolute',
});

const bodyStyles = xcss({
	fontSize: '14px',
	lineHeight: `${SUMMARY_LINE_HEIGHT}px`,
	flexGrow: 1,
});

const collapsedStyles = xcss({
	height: `${SUMMARY_LINE_HEIGHT * 5}px`,
	overflow: 'hidden',
});

type ContentBlurProps = {
	contentId: string;
	summaryId?: string;
	shouldOpenInCollapsedView?: boolean;
	analyticsProperties: SummaryAnalyticsPropertiesType;
	children: ReactNode;
	onShowMore?: () => void;
};

export const ContentBlur: FC<ContentBlurProps> = ({
	contentId,
	summaryId,
	shouldOpenInCollapsedView = false,
	analyticsProperties,
	children,
	onShowMore,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [isCollapsed, setIsCollapsed] = useState(shouldOpenInCollapsedView);

	const handleShowMoreClick = () => {
		if (isCollapsed) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticsProperties.source,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'summaryShowMoreButton',
					attributes: {
						contentId,
						summaryId,
						...analyticsProperties.additionalAnalyticsAttributes,
					},
				},
			}).fire();
			setIsCollapsed(false);
			onShowMore?.();
		}
	};

	return (
		<Box
			onClick={handleShowMoreClick}
			testId="summary-blur-container"
			xcss={[isCollapsed && collapsedContainerPadding]}
		>
			<Flex alignItems="end" xcss={flexContainerStyles}>
				<Box
					xcss={[bodyStyles, isCollapsed && collapsedStyles]}
					testId="summary-blur-content-container"
				>
					{children}
				</Box>
				{isCollapsed && (
					<Inline xcss={contentBlurStyles} grow="fill" alignInline="end">
						<Button appearance="link" spacing="none">
							&#8230;{intl.formatMessage(i18n.showMore)}
						</Button>
					</Inline>
				)}
			</Flex>
		</Box>
	);
};
